import axios from "axios";

// Resolve the API base URL.
// - In production we ALWAYS use the same origin the page is loaded from (e.g., portal.readyprospect.ai)
//   so cookies are first-party and not blocked by third-party cookie policies (Chrome default).
// - REACT_APP_BACKEND_URL is honored only when its hostname matches the current page (e.g., local dev).
const _envBackend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
function _resolveApiBase() {
  if (typeof window === "undefined") return _envBackend;
  try {
    const envHost = _envBackend ? new URL(_envBackend).host : "";
    if (envHost && envHost === window.location.host) return _envBackend;
  } catch (_) { /* fall through */ }
  return window.location.origin;
}
export const API_BASE = _resolveApiBase();
export const API = `${API_BASE}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Auto-refresh interceptor: if a request gets 401, try refreshing the token and retry
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retrying and not the refresh/login endpoint itself
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/login") &&
      !originalRequest.url?.includes("/auth/refresh") &&
      !originalRequest.url?.includes("/auth/me")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Refresh failed — redirect to login
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Agencies
export const getAgencies = () => axios.get(`${API}/agencies`);
export const createAgency = (data) => axios.post(`${API}/agencies`, data);
export const getAgency = (id) => axios.get(`${API}/agencies/${id}`);
export const uploadAgencyLogo = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return axios.post(`${API}/agencies/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
};
export const updateAgencyBranding = (id, body) => axios.patch(`${API}/agencies/${id}/branding`, body);
export const downloadProspectMaxDeckUrl = () => `${API}/decks/prospectmax`;
export const updateUserProfile = (id, body) => axios.patch(`${API}/users/${id}/profile`, body);
export const deleteAgency = (id) => axios.delete(`${API}/agencies/${id}`);
export const getAgencyUsers = (id) => axios.get(`${API}/agencies/${id}/users`);
export const inviteUserToAgency = (agencyId, data) => axios.post(`${API}/agencies/${agencyId}/users/invite`, data);

// Users
export const getUsers = () => axios.get(`${API}/users`);
export const inviteUser = (data) => axios.post(`${API}/users/invite`, data);
export const deleteUser = (id) => axios.delete(`${API}/users/${id}`);
export const getUserClients = (id) => axios.get(`${API}/users/${id}/clients`);
export const getAgencyClients = (id) => axios.get(`${API}/agencies/${id}/clients`);

// Clients
export const getClients = () => axios.get(`${API}/clients`);
export const createClient = (data) => axios.post(`${API}/clients`, data);
export const getClient = (id) => axios.get(`${API}/clients/${id}`);
export const deleteClient = (id) => axios.delete(`${API}/clients/${id}`);

// Orders/Projects
export const createOrder = (data) => axios.post(`${API}/orders`, data);
export const getProjects = () => axios.get(`${API}/projects`);
export const getProject = (id) => axios.get(`${API}/projects/${id}`);
export const updateProjectStatus = (id, status) => axios.patch(`${API}/projects/${id}/status`, { status });
export const updateProjectNotes = (id, notes) => axios.patch(`${API}/projects/${id}/notes`, { notes });
export const setRenewalDismissed = (id, dismissed) => axios.patch(`${API}/projects/${id}/renewal-dismissed`, { dismissed });
export const verifyProject = (id) => axios.post(`${API}/projects/${id}/verify`, {});
export const markProjectLive = (id) => axios.post(`${API}/projects/${id}/mark-live`, {});
export const toggleDailySummary = (id, disabled) => axios.patch(`${API}/users/${id}/daily-summary`, { disabled });
export const updateNotifyProductTypes = (userId, productTypes) =>
  axios.patch(`${API}/users/${userId}/notify-product-types`, { notify_product_types: productTypes });
export const updateNotificationEmail = (userId, body) =>
  axios.patch(`${API}/users/${userId}/notification-email`, body);
export const updateUserRole = (userId, role) =>
  axios.patch(`${API}/users/${userId}/role`, { role });
export const nudgeUser = (userId) => axios.post(`${API}/users/${userId}/nudge`, {});
export const adminSetPassword = (userId, password) =>
  axios.post(`${API}/admin/users/${userId}/set-password`, { password });
export const adminLoginDiagnostic = (userId) =>
  axios.get(`${API}/admin/users/${userId}/login-diagnostic`);
export const createMigratedOrder = (payload) => axios.post(`${API}/orders/migrated`, payload);
export const addTaskToProject = (projectId, data) => axios.post(`${API}/projects/${projectId}/tasks`, data);

// Tasks
export const getTasks = (taskType) => axios.get(`${API}/tasks`, { params: { task_type: taskType } });
export const updateTaskStatus = (id, status) => axios.patch(`${API}/tasks/${id}/status`, { status });

// Dashboard
export const getDashboardStats = () => axios.get(`${API}/dashboard/stats`);
export const getRecentActivity = () => axios.get(`${API}/dashboard/recent-activity`);

// Notifications
export const getNotifications = () => axios.get(`${API}/notifications`);
export const markNotificationRead = (id) => axios.patch(`${API}/notifications/${id}/read`);
export const markAllNotificationsRead = () => axios.patch(`${API}/notifications/read-all`);

// Product Types
export const getProductTypes = () => axios.get(`${API}/product-types`);

// File Upload
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return axios.post(`${API}/files/upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const getFileInfo = (fileId) => axios.get(`${API}/files/${fileId}/info`);
export const downloadFile = (fileId) => axios.get(`${API}/files/${fileId}/download`, { responseType: "blob" });

// Intake Forms
export const submitIntakeForm = (data) => axios.post(`${API}/intake-forms`, data);
export const getIntakeForms = (params) => axios.get(`${API}/intake-forms`, { params });
export const getIntakeForm = (id) => axios.get(`${API}/intake-forms/${id}`);
export const shareIntakeForm = (data) => axios.post(`${API}/intake-forms/share`, data);
export const getPendingIntakeRequests = (params) => axios.get(`${API}/intake-forms/pending`, { params });
export const resendIntakeShare = (token) => axios.post(`${API}/intake-forms/share/${token}/resend`, {});
export const cancelIntakeShare = (token, reason = "rep_downloaded_docx") =>
  axios.post(`${API}/intake-forms/share/${token}/cancel`, { reason });

// Messages (unified inbox)
export const getMessageConversations = () => axios.get(`${API}/messages/conversations`);
export const getClientMessages = (clientId) => axios.get(`${API}/messages/client/${clientId}`);
export const markClientMessagesRead = (clientId) => axios.post(`${API}/messages/mark-read/${clientId}`, {});
export const markClientHandled = (clientId) => axios.post(`${API}/messages/mark-handled/${clientId}`, {});
export const unmarkClientHandled = (clientId) => axios.delete(`${API}/messages/mark-handled/${clientId}`);
export const getMessagesUnreadCount = () => axios.get(`${API}/messages/unread-count`);
export const postProjectComment = (projectId, body, fileIds = [], internalOnly = false) =>
  axios.post(`${API}/comments`, { thread_type: "project", thread_id: projectId, body, file_ids: fileIds, internal_only: internalOnly });
export const postChangeRequestComment = (crId, body, fileIds = [], internalOnly = false) =>
  axios.post(`${API}/comments`, { thread_type: "change_request", thread_id: crId, body, file_ids: fileIds, internal_only: internalOnly });
export const getMentionableUsers = () => axios.get(`${API}/users/mentionable`);

// AEO/SEO Audits
export const createAudit = (body) => axios.post(`${API}/audits`, body, { timeout: 180000 });
export const listAudits = () => axios.get(`${API}/audits`);
export const getAudit = (id) => axios.get(`${API}/audits/${id}`);
export const audit_download_url = (id, format) => `${API}/audits/${id}/download?format=${format}`;
export const sendAuditEmail = (id, body) => axios.post(`${API}/audits/${id}/send`, body);
export const listAuditSends = () => axios.get(`${API}/audit-sends`);

// Tasks board (admin dashboard)
export const getTaskBoard = (limit = 50) => axios.get(`${API}/tasks/board`, { params: { limit } });

// Resources
export const getResources = () => axios.get(`${API}/resources`);
export const createResource = (data) => axios.post(`${API}/resources`, data);
export const deleteResource = (id) => axios.delete(`${API}/resources/${id}`);
export const updateResource = (id, data) => axios.patch(`${API}/resources/${id}`, data);
export const uploadResourceFile = (formData, onUploadProgress) =>
  axios.post(`${API}/resources/upload`, formData, { onUploadProgress });
export const announceResources = (data) => axios.post(`${API}/resources/announce`, data);
export const getResourceFolders = () => axios.get(`${API}/resource-folders`);
export const createResourceFolder = (data) => axios.post(`${API}/resource-folders`, data);
export const renameResourceFolder = (id, name) => axios.patch(`${API}/resource-folders/${id}`, { name });
export const deleteResourceFolder = (id) => axios.delete(`${API}/resource-folders/${id}`);

// Homepage Mockup Builder — Claude Sonnet 4.5 generates a self-contained
// HTML hero page for a prospect. POST returns immediately (status="running")
// and the caller polls GET /api/mockups/{id} until status becomes
// "ready" | "failed".
export const listMockups = (params = {}) => axios.get(`${API}/mockups`, { params });
export const createMockup = (data) => axios.post(`${API}/mockups`, data);
export const getMockup = (id) => axios.get(`${API}/mockups/${id}`);
export const regenerateMockup = (id, data) => axios.post(`${API}/mockups/${id}/regenerate`, data || {});
export const refineMockup = (id, instruction) => axios.post(`${API}/mockups/${id}/refine`, { instruction });
export const deleteMockup = (id) => axios.delete(`${API}/mockups/${id}`);
export const uploadMockupAsset = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return axios.post(`${API}/mockups/upload-asset`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const generateMockupHeroImage = (data) => axios.post(`${API}/mockups/generate-hero-image`, data);
export const stockPhotoSearch = (q, per_page = 12, page = 1) =>
  axios.get(`${API}/mockups/stock-search`, { params: { q, per_page, page } });
export const importStockPhoto = (data) => axios.post(`${API}/mockups/import-stock`, data);
export const analyzeProspectWebsite = (url) => axios.post(`${API}/mockups/analyze-website`, { url });
export const emailMockupToProspect = (id, data) => axios.post(`${API}/mockups/${id}/email`, data);
export const getEmailDiagnostics = () => axios.get(`${API}/admin/email-diagnostics`);
// URL helpers — used directly in <a href> for public preview + downloads
export const mockupPreviewUrl = (id) => `${API}/mockups/${id}/preview`;
export const mockupHtmlUrl = (id) => `${API}/mockups/${id}/download.html`;
export const mockupPdfUrl = (id) => `${API}/mockups/${id}/download.pdf`;
export const mockupPngUrl = (id) => `${API}/mockups/${id}/download.png`;
export const mockupAssetUrl = (path) => (path?.startsWith("http") ? path : `${API_BASE}${path}`);
// Public (no-auth) intake helpers
export const getPublicIntakeForm = (token) => axios.get(`${API}/public/intake/${token}`, { withCredentials: false });
export const submitPublicIntakeForm = (token, data) =>
  axios.post(`${API}/public/intake/${token}/submit`, data, { withCredentials: false });
export const uploadPublicIntakeFile = (token, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return axios.post(`${API}/public/intake/${token}/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    withCredentials: false,
  });
};
export const sendIntakeInstructions = (data) => axios.post(`${API}/intake-forms/send-instructions`, data);

// Download a fillable Word doc of an intake form (white-labeled with the rep's
// agency logo + name). The schema is passed from the frontend so we keep one
// canonical source of truth in /lib/intakeForms.js.
export const downloadIntakeFormDocx = async ({ schema, formName, agencyId, senderLabel } = {}) => {
  const res = await axios.post(
    `${API}/intake-forms/template/docx`,
    {
      schema,
      form_name: formName || schema?.name,
      agency_id: agencyId,
      sender_label: senderLabel,
    },
    { responseType: "blob" }
  );
  const url = window.URL.createObjectURL(new Blob([res.data], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  }));
  const filename = `${(formName || schema?.name || "intake-form").replace(/[^A-Za-z0-9._-]+/g, "_")}.docx`;
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

// Operations
export const getOperationsKanban = () => axios.get(`${API}/operations/kanban`);
export const updateTaskDueDate = (taskId, dueDate) => axios.patch(`${API}/tasks/${taskId}/due-date`, { due_date: dueDate });
export const sendDailySummary = () => axios.post(`${API}/admin/send-daily-summary`);

// Admin Settings - Product Catalog
export const getProductCatalog = () => axios.get(`${API}/admin/product-catalog`);
export const updateProductCatalog = (productId, data) => axios.put(`${API}/admin/product-catalog/${productId}`, data);
export const createProductCatalog = (data) => axios.post(`${API}/admin/product-catalog`, data);
export const deleteProductCatalog = (productId) => axios.delete(`${API}/admin/product-catalog/${productId}`);

// Billing
export const getPendingBilling = () => axios.get(`${API}/billing/pending`);
export const markAsBilled = (projectId) => axios.patch(`${API}/billing/${projectId}/mark-billed`);
export const unmarkBilled = (projectId) => axios.patch(`${API}/billing/${projectId}/unbill`);

// Proposals
export const createProposal = (data) => axios.post(`${API}/proposals`, data);
export const getProposals = (params) => {
  // Backwards compat: accept either a string (status) or an object of params.
  const opts = typeof params === "string" ? { status: params } : (params || {});
  return axios.get(`${API}/proposals`, { params: opts });
};

// Project lifecycle
export const updateProjectLifecycle = (id, stage) => axios.patch(`${API}/projects/${id}/lifecycle`, { lifecycle_stage: stage });
export const getExpiringProjects = (days = 30) => axios.get(`${API}/projects/expiring`, { params: { days } });

// Change requests
export const createChangeRequest = (data) => axios.post(`${API}/change-requests`, data);
export const getChangeRequests = (clientId) => axios.get(`${API}/change-requests`, { params: clientId ? { client_id: clientId } : {} });
export const updateChangeRequestStatus = (id, status) => axios.patch(`${API}/change-requests/${id}/status`, { status });

// Comments / threads
export const getComments = (threadType, threadId) => axios.get(`${API}/comments`, { params: { thread_type: threadType, thread_id: threadId } });
export const createComment = (data) => axios.post(`${API}/comments`, data);
export const getProposalDetail = (id) => axios.get(`${API}/proposals/${id}`);
export const updateProposalStatus = (id, status) => axios.patch(`${API}/proposals/${id}/status`, { status });
export const deleteProposal = (id) => axios.delete(`${API}/proposals/${id}`);
export const deleteProject = (id) => axios.delete(`${API}/projects/${id}`);
export const getProposalsSummary = () => axios.get(`${API}/proposals/summary`);
export const attachProposalFile = (id, fileId) => axios.post(`${API}/proposals/${id}/attach`, { file_id: fileId });
export const emailProposalToRep = (id, message) => axios.post(`${API}/proposals/${id}/email-to-rep`, { message });

// Bulk Import
export const bulkImportClients = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return axios.post(`${API}/admin/bulk-import-clients`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const downloadBulkImportTemplate = () =>
  axios.get(`${API}/admin/bulk-import-template`, { responseType: "blob" });

// Impersonation (Super Admin only)
export const startImpersonation = (userId) =>
  axios.post(`${API}/auth/impersonate/${userId}`, {}, { withCredentials: true });
export const stopImpersonation = () =>
  axios.post(`${API}/auth/impersonate/stop`, {}, { withCredentials: true });

// Password management
export const changePassword = (currentPassword, newPassword) =>
  axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword });
export const adminResetPassword = (userId) =>
  axios.post(`${API}/admin/users/${userId}/reset-password`, {});
export const adminClearLockout = (userId) =>
  axios.post(`${API}/admin/users/${userId}/clear-lockout`, {});
export const adminResendInvite = (userId) =>
  axios.post(`${API}/admin/users/${userId}/resend-invite`, {});
export const forgotPassword = (email) =>
  axios.post(`${API}/auth/forgot-password`, { email });
export const resetPassword = (token, newPassword) =>
  axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });

// Unified Inbox
export const getUnifiedInbox = (assignee = "all") =>
  axios.get(`${API}/inbox`, { params: { assignee } });

// Admin: stale-task cleanup
export const cleanupMonthlyActiveTasks = (dryRun = true) =>
  axios.post(`${API}/admin/cleanup-monthly-active-tasks`, {}, { params: { dry_run: dryRun } });
export const undoCleanupMonthlyActiveTasks = () =>
  axios.post(`${API}/admin/cleanup-monthly-active-tasks/undo`, {});

// Admin: template retrofit — re-align pending tasks on active projects
// to the current (shorter) Feb 2026 templates. Safe: preserves any task
// with an assignee, comments, or progress; only deletes untouched pending
// tasks whose title isn't in the current template.
export const retrofitTaskTemplates = (dryRun = true) =>
  axios.post(`${API}/admin/retrofit-task-templates`, {}, { params: { dry_run: dryRun } });
export const undoRetrofitTaskTemplates = () =>
  axios.post(`${API}/admin/retrofit-task-templates/undo`, {});



// Direct messages — general team ↔ rep/manager threads not tied to a client.
export const createDirectThread = (payload) =>
  axios.post(`${API}/direct-threads`, payload);
export const listDirectThreads = () =>
  axios.get(`${API}/direct-threads`);
export const getDirectThread = (id) =>
  axios.get(`${API}/direct-threads/${id}`);
export const markDirectThreadHandled = (id) =>
  axios.post(`${API}/direct-threads/${id}/mark-handled`);
